import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Table, Form, Button, ButtonGroup, Card } from 'react-bootstrap';
import { TimesheetEntry } from '../../api/generated/model';
import { PayrollActivity } from '../../api/generated/model';
import { parseHoursInput, formatHours } from '../../utils/hours';

export interface EntryData {
  activity_id: string;
  entry_date: string; // YYYY-MM-DD
  hours_decimal?: number;
  hours_minutes?: string;
  description: string;
}

interface CellData {
  hours: number;
  rawHours: string;
  description: string;
}

export type ViewMode = 'day' | 'month';

interface TimesheetWeeklyGridProps {
  entries: TimesheetEntry[];
  activities: PayrollActivity[];
  periodStart: string;
  periodEnd: string;
  reviewMode: boolean;
  onChange: (entries: EntryData[]) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  defaultHourlyRate?: number;
}

const NUM_COLUMNS_FULL = 5;
const NUM_COLUMNS_SIMPLE = 2; // Hours + Description only (no activity/rate/amount)

// Generate array of dates between start and end inclusive
const getDateRange = (start: string, end: string): string[] => {
  const dates: string[] = [];
  const current = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  while (current <= endDate) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const getDayLabel = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const dow = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const dayNum = date.getDate();
  const year = date.getFullYear();
  return `${dow} ${month} ${dayNum}, ${year}`;
};

// --- Reusable day entry block ---
// Renders a date header row + one activity row per selected activity.
// Used by all three views (day, week, period).
interface DayEntryBlockProps {
  date: string;
  activityIds: string[];
  availableActivities: PayrollActivity[];
  gridData: Record<string, Record<string, CellData>>;
  getActivityName: (id: string) => string;
  getActivityRate: (id: string, date: string) => number;
  getColumnTotal: (date: string) => number;
  getColumnAmount: (date: string) => number;
  onHoursChange: (activityId: string, date: string, value: string) => void;
  onHoursBlur: (activityId: string, date: string) => void;
  onDescriptionChange: (activityId: string, date: string, description: string) => void;
  onAddActivityToDay?: (activityId: string, date: string) => void;
  onRemoveActivityFromDay?: (activityId: string, date: string) => void;
  onActivityChange?: (oldActId: string, newActId: string, date: string) => void;
  reviewMode: boolean;
  prevDate?: string;
  noActivities?: boolean;
}

const isWeekend = (dateStr: string): boolean => {
  const day = new Date(dateStr + 'T00:00:00').getDay();
  return day === 0 || day === 6;
};

// Determine if a week boundary (Mon or Sat) falls between two dates
const hasWeekBoundaryBetween = (prevDate: string, curDate: string): boolean => {
  const prev = new Date(prevDate + 'T00:00:00');
  const cur = new Date(curDate + 'T00:00:00');
  const d = new Date(prev);
  d.setDate(d.getDate() + 1);
  while (d <= cur) {
    const dow = d.getDay();
    if (dow === 1 || dow === 6) return true;
    d.setDate(d.getDate() + 1);
  }
  return false;
};

const DayEntryBlock: React.FC<DayEntryBlockProps> = ({
  date,
  activityIds,
  availableActivities,
  gridData,
  getActivityName,
  getActivityRate,
  getColumnTotal,
  getColumnAmount,
  onHoursChange,
  onHoursBlur,
  onDescriptionChange,
  onAddActivityToDay,
  onRemoveActivityFromDay,
  onActivityChange,
  reviewMode,
  prevDate,
  noActivities,
}) => {
  const dayTotal = getColumnTotal(date);
  const dayAmount = getColumnAmount(date);
  const weekend = isWeekend(date);
  const numColumns = noActivities ? NUM_COLUMNS_SIMPLE : NUM_COLUMNS_FULL;
  // Show separator when a week boundary (Monday or Saturday) falls between the previous visible date and this one
  const showSeparator = prevDate ? hasWeekBoundaryBetween(prevDate, date) : false;
  // Activities available to add for this specific day (exclude ones already showing)
  const dayAvailable = noActivities ? [] : availableActivities.filter(a => !activityIds.includes(a.id || ''));
  return (
    <>
      {showSeparator && (
        <tr>
          <td colSpan={numColumns} className="border-top-0 border-bottom-0 py-3">
            <hr className="m-0" />
          </td>
        </tr>
      )}
      <tr className="table-active">
        <td colSpan={numColumns} className={weekend ? 'fst-italic text-muted' : 'fw-semibold'}>
          <div className="d-flex justify-content-between">
            <span>{getDayLabel(date)}</span>
            <span>{formatHours(dayTotal)} hrs{!noActivities && <> &middot; ${dayAmount.toFixed(2)}</>}</span>
          </div>
        </td>
      </tr>
      {activityIds.map(actId => {
        const cell = gridData[actId]?.[date] || { hours: 0, rawHours: '', description: '' };
        const rate = getActivityRate(actId, date);
        const lineAmount = cell.hours * rate;
        // Other activity IDs already assigned to this day (for filtering dropdown options)
        const otherActIdsOnDay = activityIds.filter(id => id !== actId);
        // Activities available for this row's dropdown: all active activities not already used on this day by other rows
        const rowAvailable = availableActivities.filter(a => !otherActIdsOnDay.includes(a.id || ''));
        return (
          <tr key={`${date}-${actId}`} className={weekend ? 'text-muted' : ''}>
            {!noActivities && (
              <td className="align-middle ps-4 p-1">
                <div className="d-flex align-items-center gap-1">
                  {!reviewMode && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="me-1 flex-shrink-0"
                      onClick={() => onRemoveActivityFromDay?.(actId, date)}
                      title="Remove activity from this day"
                    >
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  )}
                  {reviewMode ? (
                    <span className="fw-medium">{getActivityName(actId)}</span>
                  ) : (() => {
                    const isInvalid = !rowAvailable.some(a => a.id === actId);
                    return (
                      <Form.Select
                        size="sm"
                        value={actId}
                        isInvalid={isInvalid}
                        onChange={e => {
                          if (e.target.value !== actId) {
                            onActivityChange?.(actId, e.target.value, date);
                          }
                        }}
                      >
                        {isInvalid && (
                          <option value={actId} disabled>-- Select Activity --</option>
                        )}
                        {rowAvailable.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </Form.Select>
                    );
                  })()}
                </div>
              </td>
            )}
            <td className="p-1 align-middle" style={{ width: '80px' }}>
              {reviewMode ? (
                <span className="d-block text-center">{cell.rawHours || '0'}</span>
              ) : (
                <Form.Control
                  type="text"
                  value={cell.rawHours}
                  onChange={e => onHoursChange(actId, date, e.target.value)}
                  onBlur={() => onHoursBlur(actId, date)}
                  className="text-center"
                  placeholder="0"
                />
              )}
            </td>
            <td className="p-1 align-middle">
              {reviewMode ? (
                <span style={{ whiteSpace: 'pre-line' }}>{cell.description}</span>
              ) : (
                <Form.Control
                  as="textarea"
                  rows={1}
                  value={cell.description}
                  onChange={e => onDescriptionChange(actId, date, e.target.value)}
                  placeholder="Description..."
                  style={{ resize: 'vertical' }}
                />
              )}
            </td>
            {!noActivities && (
              <>
                <td className="text-end text-muted align-middle text-nowrap" style={{ width: '80px' }}>
                  ${rate.toFixed(2)}/hr
                </td>
                <td className="text-end align-middle text-nowrap" style={{ width: '80px' }}>
                  ${lineAmount.toFixed(2)}
                </td>
              </>
            )}
          </tr>
        );
      })}
      {!reviewMode && dayAvailable.length > 0 && (
        <tr>
          <td colSpan={numColumns} className="p-1">
            <Form.Select
              size="sm"
              value=""
              onChange={e => {
                if (e.target.value) onAddActivityToDay?.(e.target.value, date);
              }}
              style={{ maxWidth: '300px' }}
            >
              <option value="">+ Add Activity...</option>
              {dayAvailable.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </Form.Select>
          </td>
        </tr>
      )}
    </>
  );
};

// --- Reusable table wrapper that renders DayEntryBlocks for a list of dates ---
interface DateListTableProps {
  dates: string[];
  gridData: Record<string, Record<string, CellData>>;
  availableActivities: PayrollActivity[];
  getActivityName: (id: string) => string;
  getActivityRate: (id: string, date: string) => number;
  getColumnTotal: (date: string) => number;
  getColumnAmount: (date: string) => number;
  onHoursChange: (activityId: string, date: string, value: string) => void;
  onHoursBlur: (activityId: string, date: string) => void;
  onDescriptionChange: (activityId: string, date: string, description: string) => void;
  onAddActivityToDay?: (activityId: string, date: string) => void;
  onRemoveActivityFromDay?: (activityId: string, date: string) => void;
  onActivityChange?: (oldActId: string, newActId: string, date: string) => void;
  reviewMode: boolean;
  noActivities?: boolean;
}

const DateListTable: React.FC<DateListTableProps> = ({
  dates,
  gridData,
  availableActivities,
  getActivityName,
  getActivityRate,
  getColumnTotal,
  getColumnAmount,
  onHoursChange,
  onHoursBlur,
  onDescriptionChange,
  onAddActivityToDay,
  onRemoveActivityFromDay,
  onActivityChange,
  reviewMode,
  noActivities,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollUp(el.scrollTop > 0);
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState, gridData]);

  return (
    <div>
      {!reviewMode && (
        <div className="text-muted small mb-2">
          Hours may be entered in decimal (i.e. 3.5) or HH:mm (i.e. 3:30).
        </div>
      )}
      <Card style={{ overflow: 'hidden', position: 'relative' }}>
        {canScrollUp && (
          <div style={{
            position: 'absolute', top: 37, left: 0, right: 0, height: '16px',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), transparent)',
            zIndex: 3, pointerEvents: 'none',
          }} />
        )}
        {canScrollDown && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '16px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.15), transparent)',
            zIndex: 3, pointerEvents: 'none',
          }} />
        )}
      <Card.Body className="p-0" style={{ maxHeight: '50vh', overflowY: 'auto' }} ref={scrollRef} onScroll={updateScrollState}>
        <Table className="mb-0">
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              {!noActivities && <th style={{ minWidth: '140px', backgroundColor: 'var(--bs-body-bg)' }}>Activity</th>}
              <th style={{ width: '80px', backgroundColor: 'var(--bs-body-bg)' }} className="text-center">Hours</th>
              <th style={{ backgroundColor: 'var(--bs-body-bg)' }}>Description</th>
              {!noActivities && <th style={{ width: '80px', backgroundColor: 'var(--bs-body-bg)' }} className="text-end">Rate</th>}
              {!noActivities && <th style={{ width: '80px', backgroundColor: 'var(--bs-body-bg)' }} className="text-end">Amount</th>}
            </tr>
          </thead>
          <tbody>
            {dates.reduce<{ elements: React.ReactNode[]; lastDate?: string }>((acc, date) => {
              // Activities visible on this day = those with a cell in gridData for this date
              const dayActivityIds = Object.keys(gridData).filter(
                actId => gridData[actId]?.[date] !== undefined
              );
              // In review mode, skip days with no hours and no descriptions
              if (reviewMode && getColumnTotal(date) === 0 && !dayActivityIds.some(actId => gridData[actId]?.[date]?.description)) return acc;
              acc.elements.push(
                <DayEntryBlock
                  key={date}
                  date={date}
                  activityIds={dayActivityIds}
                  availableActivities={availableActivities}
                  gridData={gridData}
                  getActivityName={getActivityName}
                  getActivityRate={getActivityRate}
                  getColumnTotal={getColumnTotal}
                  getColumnAmount={getColumnAmount}
                  onHoursChange={onHoursChange}
                  onHoursBlur={onHoursBlur}
                  onDescriptionChange={onDescriptionChange}
                  onAddActivityToDay={onAddActivityToDay}
                  onRemoveActivityFromDay={onRemoveActivityFromDay}
                  onActivityChange={onActivityChange}
                  reviewMode={reviewMode}
                  prevDate={acc.lastDate}
                  noActivities={noActivities}
                />
              );
              acc.lastDate = date;
              return acc;
            }, { elements: [] }).elements}
          </tbody>
        </Table>
      </Card.Body>
      </Card>
    </div>
  );
};

// seedGrid turns the server's flat entry list into the grid's cell map.
const seedGrid = (entries: TimesheetEntry[]): Record<string, Record<string, CellData>> => {
  const data: Record<string, Record<string, CellData>> = {};
  for (const entry of entries) {
    const actId = entry.activity_id || '';
    const date = entry.entry_date || '';
    let hours = 0;
    let rawHours = '';
    if (entry.hours_minutes) {
      rawHours = entry.hours_minutes;
      hours = parseHoursInput(entry.hours_minutes);
    } else if (entry.hours_decimal != null && entry.hours_decimal > 0) {
      rawHours = String(entry.hours_decimal);
      hours = entry.hours_decimal;
    } else if (entry.hours && entry.hours > 0) {
      // Backward compat: old entries with only hours field
      rawHours = String(entry.hours);
      hours = entry.hours;
    }
    if (!data[actId]) data[actId] = {};
    data[actId][date] = {
      hours,
      rawHours,
      description: entry.description || '',
    };
  }
  return data;
};

// entriesSignature captures only what seedGrid reads, so a refetch that returns
// the same hours does not count as a change and cannot discard an edit in
// progress. Sorted, because the server's ordering is not part of the meaning.
const entriesSignature = (entries: TimesheetEntry[]): string =>
  JSON.stringify(
    entries
      .map(e => [
        e.activity_id || '',
        e.entry_date || '',
        e.hours_minutes || '',
        e.hours_decimal ?? null,
        e.hours ?? null,
        e.description || '',
      ])
      .sort()
  );

// --- Main component ---

const TimesheetWeeklyGrid: React.FC<TimesheetWeeklyGridProps> = ({
  entries,
  activities,
  periodStart,
  periodEnd,
  reviewMode,
  onChange,
  viewMode: externalViewMode,
  onViewModeChange,
  defaultHourlyRate,
}) => {
  const allDates = useMemo(() => getDateRange(periodStart, periodEnd), [periodStart, periodEnd]);
  const noActivities = false;

  // Local state: map of activityId -> { date -> CellData }
  // A cell existing in gridData means it's visible on the grid.
  // When no activities are available, use '' as the activity key with one row per day.
  const [gridData, setGridData] = useState<Record<string, Record<string, CellData>>>(() => seedGrid(entries));

  // Re-seed when the entries prop actually changes.
  //
  // The seed above runs once, at mount. That made the grid silently dependent
  // on its caller handing it final data on the very first render: a parent that
  // mounted it while the entries were still loading got an empty grid that
  // never filled in, showing every day as zero over a timesheet that had hours.
  // The signature ref is what keeps this from fighting the user — a refetch
  // that returns identical data re-seeds nothing, so typing is not clobbered.
  const seededSignature = useRef<string>(entriesSignature(entries));
  useEffect(() => {
    const signature = entriesSignature(entries);
    if (signature === seededSignature.current) return;
    seededSignature.current = signature;
    setGridData(seedGrid(entries));
  }, [entries]);

  const [internalViewMode, setInternalViewMode] = useState<ViewMode>('month');
  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = onViewModeChange ?? setInternalViewMode;

  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);

  // Build rate lookup:
  // - Editable (draft/rejected): compute effective rate per entry date from activity rate schedules
  // - Read-only (submitted/approved/paid): use locked-in entry.hourly_rate from server
  const entryRateLookup = useMemo(() => {
    if (!reviewMode) return null;
    const lookup: Record<string, number> = {};
    for (const entry of entries) {
      if (entry.entry_date && entry.hourly_rate != null) {
        const actId = entry.activity_id || '';
        lookup[`${actId}:${entry.entry_date}`] = entry.hourly_rate;
      }
    }
    return lookup;
  }, [entries, reviewMode]);

  const getActivityRate = useCallback((activityId: string, date: string): number => {
    // Review mode: use locked-in rate from server entry only
    if (reviewMode) {
      if (!entryRateLookup) return 0;
      return entryRateLookup[`${activityId}:${date}`] ?? 0;
    }
    // Edit mode: user default hourly rate wins if set
    if (defaultHourlyRate != null) return defaultHourlyRate;
    // Then activity rate schedule
    const activity = activities.find(a => a.id === activityId);
    if (!activity?.rates?.length) return 0;
    for (let i = activity.rates.length - 1; i >= 0; i--) {
      const r = activity.rates[i];
      if (r.start_date && r.start_date <= date) return r.hourly_rate ?? 0;
    }
    return 0;
  }, [activities, entryRateLookup, defaultHourlyRate, reviewMode]);

  // Build the flat entries array from gridData and call onChange.
  // gridData is the single source of truth: if a cell exists, it's an entry.
  const emitChange = useCallback((newGridData: Record<string, Record<string, CellData>>) => {
    const result: EntryData[] = [];
    for (const actId of Object.keys(newGridData)) {
      for (const [date, cell] of Object.entries(newGridData[actId])) {
        const entry: EntryData = {
          activity_id: actId,
          entry_date: date,
          description: cell.description,
        };
        if (cell.rawHours.includes(':')) {
          const parts = cell.rawHours.split(':');
          const m = (parts[1] || '0').padStart(2, '0');
          entry.hours_minutes = `${parts[0]}:${m}`;
        } else {
          entry.hours_decimal = Math.round(cell.hours * 100) / 100;
        }
        result.push(entry);
      }
    }
    onChange(result);
  }, [onChange]);

  const handleHoursChange = (activityId: string, date: string, value: string) => {
    // Allow clearing the field
    if (!value.trim()) {
      setGridData(prev => {
        const updated = { ...prev };
        if (!updated[activityId]) updated[activityId] = {};
        updated[activityId] = {
          ...updated[activityId],
          [date]: { ...(updated[activityId][date] || { hours: 0, rawHours: '', description: '' }), hours: 0, rawHours: '' },
        };
        emitChange(updated);
        return updated;
      });
      return;
    }
    // Allow partial typing (e.g. "3:" before minutes are entered)
    if (value.endsWith(':')) {
      setGridData(prev => {
        const updated = { ...prev };
        if (!updated[activityId]) updated[activityId] = {};
        updated[activityId] = {
          ...updated[activityId],
          [date]: { ...(updated[activityId][date] || { hours: 0, rawHours: '', description: '' }), rawHours: value },
        };
        return updated;
      });
      return;
    }
    const hours = parseHoursInput(value);
    // Reject invalid input (e.g. 3:99)
    if (isNaN(hours) || hours < 0 || hours > 24) return;
    setGridData(prev => {
      const updated = { ...prev };
      if (!updated[activityId]) updated[activityId] = {};
      updated[activityId] = {
        ...updated[activityId],
        [date]: { ...(updated[activityId][date] || { hours: 0, rawHours: '', description: '' }), hours, rawHours: value },
      };
      emitChange(updated);
      return updated;
    });
  };

  const handleHoursBlur = (activityId: string, date: string) => {
    setGridData(prev => {
      const cell = prev[activityId]?.[date];
      if (!cell || !cell.rawHours.includes(':')) return prev;
      // Handle partial input like "5:" → "5:00"
      let raw = cell.rawHours;
      if (raw.endsWith(':')) raw = raw + '00';
      // Normalize hh:mm to canonical form (e.g. "5:9" → "5:09")
      const parts = raw.split(':');
      if (parts.length !== 2) return prev;
      const h = parts[0];
      const m = parts[1].padStart(2, '0');
      const canonical = `${h}:${m}`;
      if (canonical === cell.rawHours && cell.hours === parseHoursInput(canonical)) return prev;
      const hours = parseHoursInput(canonical);
      if (isNaN(hours) || hours < 0 || hours > 24) return prev;
      const updated = { ...prev };
      updated[activityId] = {
        ...updated[activityId],
        [date]: { ...cell, rawHours: canonical, hours },
      };
      emitChange(updated);
      return updated;
    });
  };

  const handleDescriptionChange = (activityId: string, date: string, description: string) => {
    setGridData(prev => {
      const updated = { ...prev };
      if (!updated[activityId]) updated[activityId] = {};
      updated[activityId] = {
        ...updated[activityId],
        [date]: { ...(updated[activityId][date] || { hours: 0, rawHours: '', description: '' }), description },
      };
      emitChange(updated);
      return updated;
    });
  };

  const addActivityToDay = (activityId: string, date: string) => {
    const actId = activityId;
    setGridData(prev => {
      const updated = { ...prev };
      if (!updated[actId]) updated[actId] = {};
      // Only create the cell if it doesn't already exist
      if (!updated[actId][date]) {
        updated[actId] = {
          ...updated[actId],
          [date]: { hours: 0, rawHours: '', description: '' },
        };
      }
      emitChange(updated);
      return updated;
    });
  };

  const removeActivityFromDay = (activityId: string, date: string) => {
    setGridData(prev => {
      const updated = { ...prev };
      if (updated[activityId]) {
        updated[activityId] = { ...updated[activityId] };
        delete updated[activityId][date];
        // Clean up activity key if no dates left
        if (Object.keys(updated[activityId]).length === 0) {
          delete updated[activityId];
        }
      }
      emitChange(updated);
      return updated;
    });
  };

  const handleActivityChange = (oldActId: string, newActId: string, date: string) => {
    setGridData(prev => {
      const updated = { ...prev };
      // Get the cell data from the old activity
      const cell = updated[oldActId]?.[date];
      if (!cell) return prev;

      // Remove the cell from the old activity
      updated[oldActId] = { ...updated[oldActId] };
      delete updated[oldActId][date];
      if (Object.keys(updated[oldActId]).length === 0) {
        delete updated[oldActId];
      }

      // Add the cell to the new activity
      if (!updated[newActId]) updated[newActId] = {};
      updated[newActId] = { ...updated[newActId], [date]: cell };

      emitChange(updated);
      return updated;
    });
  };

  const getActivityName = (activityId: string): string => {
    if (!activityId) return '';
    if (reviewMode) {
      // Review mode: use locked activity_name from backend entries
      const entry = entries.find(e => e.activity_id === activityId);
      return entry?.activity_name || '';
    }
    // Edit mode: use activities fetched for this period
    const activity = activities.find(a => a.id === activityId);
    return activity?.name || '';
  };

  const activeActivities = useMemo(() => activities.filter(a => a.active), [activities]);

  const getColumnTotal = (date: string): number => {
    return Object.keys(gridData).reduce((sum, actId) => {
      return sum + (gridData[actId]?.[date]?.hours || 0);
    }, 0);
  };

  const getColumnAmount = useCallback((date: string): number => {
    return Object.keys(gridData).reduce((sum, actId) => {
      const hours = gridData[actId]?.[date]?.hours || 0;
      return sum + hours * getActivityRate(actId, date);
    }, 0);
  }, [gridData, getActivityRate]);

  // Shared props for DateListTable
  const tableProps = {
    gridData,
    availableActivities: activeActivities,
    getActivityName,
    getActivityRate,
    getColumnTotal,
    getColumnAmount,
    onHoursChange: handleHoursChange,
    onHoursBlur: handleHoursBlur,
    onDescriptionChange: handleDescriptionChange,
    onAddActivityToDay: reviewMode ? undefined : addActivityToDay,
    onRemoveActivityFromDay: reviewMode ? undefined : removeActivityFromDay,
    onActivityChange: reviewMode ? undefined : handleActivityChange,
    reviewMode,
    noActivities,
  };

  return (
    <div>
      {/* View mode toggle (only when not controlled externally, hidden in review mode) */}
      {!externalViewMode && !reviewMode && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <ButtonGroup size="sm">
            <Button
              variant={viewMode === 'day' ? 'primary' : 'outline-primary'}
              onClick={() => setViewMode('day')}
            >
              <i className="bi bi-calendar-day me-1"></i>Day
            </Button>
            <Button
              variant={viewMode === 'month' ? 'primary' : 'outline-primary'}
              onClick={() => setViewMode('month')}
            >
              <i className="bi bi-calendar3 me-1"></i>Full Period
            </Button>
          </ButtonGroup>
        </div>
      )}

      {/* Day view: single date with nav */}
      {viewMode === 'day' && (
        <div>
          <div className="d-flex align-items-center gap-2 mb-3">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={selectedDayIndex === 0}
              onClick={() => setSelectedDayIndex(prev => prev - 1)}
            >
              <i className="bi bi-chevron-left"></i>
            </Button>
            <Form.Select
              size="sm"
              value={selectedDayIndex}
              onChange={e => setSelectedDayIndex(parseInt(e.target.value))}
              style={{ maxWidth: '200px' }}
            >
              {allDates.map((date, idx) => (
                <option key={date} value={idx}>{getDayLabel(date)}</option>
              ))}
            </Form.Select>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={selectedDayIndex >= allDates.length - 1}
              onClick={() => setSelectedDayIndex(prev => prev + 1)}
            >
              <i className="bi bi-chevron-right"></i>
            </Button>
          </div>
          {allDates[selectedDayIndex] && (
            <DateListTable dates={[allDates[selectedDayIndex]]} {...tableProps} />
          )}
        </div>
      )}

      {/* Period view: all dates */}
      {viewMode === 'month' && (
        <DateListTable dates={allDates} {...tableProps} />
      )}

    </div>
  );
};

export default TimesheetWeeklyGrid;
